import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/Badge";
import { fmtDateTime } from "../lib/format";
import { MessageSquare, Send, Trash2, Clock, Check, Sparkles, X, User } from "lucide-react";
import { toast } from "sonner";

const statusVar = (s) => (s === "open" ? "pending" : s === "closed" ? "success" : "info");
const priorityVar = (p) => (p === "high" ? "danger" : p === "low" ? "default" : "pending");

export default function Support() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState(""); // "" | "ai_chat" | "player"
  const [transcript, setTranscript] = useState(null); // { items, ticket } when open

  const load = async () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (sourceFilter === "ai_chat") params.source = "ai_chat";
    const { data } = await api.get("/support-tickets", { params });
    let list = data || [];
    // Client-side filter for "Player" (i.e. anything that isn't ai_chat) since legacy
    // tickets may have no `source` field at all.
    if (sourceFilter === "player") {
      list = list.filter((t) => (t.source || "player") !== "ai_chat");
    }
    setRows(list);
    setLoading(false);
  };
  useEffect(() => {
    load();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [statusFilter, sourceFilter]);

  const openTicket = (t) => {
    setActive(t);
    setReply(t.reply || "");
  };

  const submitReply = async () => {
    if (!active) return;
    await api.patch(`/support-tickets/${active.id}`, { reply, status: "closed" });
    toast.success("Reply sent and ticket closed");
    setActive(null);
    setReply("");
    load();
  };

  const quickUpdate = async (id, status) => {
    await api.patch(`/support-tickets/${id}`, { status });
    toast.success(`Ticket ${status}`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete ticket?")) return;
    await api.delete(`/support-tickets/${id}`);
    toast.success("Deleted");
    load();
  };

  const viewTranscript = async (t) => {
    try {
      const { data } = await api.get(`/support-tickets/${t.id}/transcript`);
      setTranscript({ items: data?.items || [], ticket: t });
    } catch {
      toast.error("Failed to load transcript");
    }
  };

  const statusTabs = [
    { k: "", label: "All" },
    { k: "open", label: "Open" },
    { k: "pending", label: "Pending" },
    { k: "closed", label: "Closed" },
  ];

  const sourceTabs = [
    { k: "", label: "All sources", icon: null },
    { k: "ai_chat", label: "AI handoff", icon: Sparkles },
    { k: "player", label: "Player-raised", icon: User },
  ];

  const aiCount = rows.filter((t) => t.source === "ai_chat").length;

  return (
    <div data-testid="support-page">
      <PageHeader title="Support Tickets" subtitle="Player-raised support requests, AI chat handoffs, and admin replies" />

      {/* Status filter row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {statusTabs.map((t) => (
          <button
            key={`s-${t.label}`}
            onClick={() => setStatusFilter(t.k)}
            data-testid={`support-tab-${t.k || "all"}`}
            className={`h-9 px-4 text-sm rounded-sm border transition-colors ${statusFilter === t.k ? "bg-[#09090B] text-white border-[#09090B]" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Source filter row */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mr-1">Source:</span>
        {sourceTabs.map((t) => {
          const Icon = t.icon;
          const isAi = t.k === "ai_chat";
          const selected = sourceFilter === t.k;
          return (
            <button
              key={`src-${t.label}`}
              onClick={() => setSourceFilter(t.k)}
              data-testid={`support-source-${t.k || "all"}`}
              className={`h-9 px-3 text-sm rounded-sm border inline-flex items-center gap-1.5 transition-colors ${
                selected
                  ? isAi
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-[#09090B] text-white border-[#09090B]"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
              }`}
            >
              {Icon ? <Icon size={13} /> : null}
              {t.label}
              {isAi && aiCount > 0 && (
                <span className={`ml-1 px-1.5 h-4 rounded-full text-[10px] font-bold inline-flex items-center ${selected ? "bg-white/20" : "bg-violet-100 text-violet-700"}`}>
                  {aiCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket list */}
        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-sm" data-testid="support-list">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
              <MessageSquare size={28} className="text-gray-300" />
              <span>No tickets in this view.</span>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[620px] overflow-y-auto">
              {rows.map((t) => {
                const isAi = t.source === "ai_chat";
                return (
                  <button
                    key={t.id}
                    onClick={() => openTicket(t)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${active?.id === t.id ? "bg-emerald-50/40" : ""}`}
                    data-testid={`support-row-${t.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] font-mono text-gray-500 flex items-center gap-1.5">
                        {isAi && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 h-4 rounded-sm bg-violet-100 text-violet-700 text-[9px] font-bold uppercase tracking-wider" data-testid="support-ai-badge">
                            <Sparkles size={9} /> AI
                          </span>
                        )}
                        {t.ticket_no}
                      </div>
                      <Badge variant={statusVar(t.status)}>{t.status}</Badge>
                    </div>
                    <div className="font-semibold text-sm text-gray-900 mt-1 line-clamp-1">{t.subject}</div>
                    <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                      {t.user_name || t.username || "—"}{t.user_email ? ` · ${t.user_email}` : ""}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <Badge variant={priorityVar(t.priority)}>{t.priority}</Badge>
                      <span className="text-[10px] text-gray-500">{fmtDateTime(t.created_at)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Ticket detail */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-sm min-h-[400px] flex flex-col" data-testid="support-detail">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500 p-8">Select a ticket to view details</div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-xs text-gray-500">{active.ticket_no}</span>
                  <Badge variant={statusVar(active.status)}>{active.status}</Badge>
                  <Badge variant={priorityVar(active.priority)}>{active.priority} priority</Badge>
                  {active.source === "ai_chat" && (
                    <span className="inline-flex items-center gap-1 px-2 h-5 rounded-sm bg-violet-100 text-violet-700 text-[10px] font-bold uppercase tracking-wider">
                      <Sparkles size={10} /> AI handoff
                    </span>
                  )}
                </div>
                <div className="font-display text-xl font-bold text-gray-900">{active.subject}</div>
                <div className="text-xs text-gray-500 mt-1">
                  from <b>{active.user_name || active.username || "—"}</b>
                  {active.user_email ? ` · ${active.user_email}` : ""}
                  {" · "}{fmtDateTime(active.created_at)}
                </div>
              </div>
              <div className="p-5 space-y-4 flex-1 overflow-y-auto">
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-4">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">User Message</div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">{active.message}</div>
                </div>

                {active.source === "ai_chat" && (
                  <button
                    onClick={() => viewTranscript(active)}
                    data-testid="support-view-transcript"
                    className="w-full px-4 py-3 rounded-sm border border-violet-200 bg-violet-50 hover:bg-violet-100 transition-colors text-left flex items-center justify-between"
                  >
                    <span className="text-sm font-semibold text-violet-700 inline-flex items-center gap-2">
                      <Sparkles size={14} /> View full AI chat transcript
                    </span>
                    <span className="text-[11px] text-violet-600">click to open →</span>
                  </button>
                )}

                {active.reply && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Admin Reply</div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{active.reply}</div>
                  </div>
                )}
                {active.status !== "closed" && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Your Reply</label>
                    <textarea
                      rows={4}
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
                      placeholder="Type your response..."
                      data-testid="support-reply-input"
                    />
                  </div>
                )}
              </div>
              <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between gap-2">
                <button onClick={() => remove(active.id)} className="h-9 px-3 border border-gray-200 rounded-sm text-sm text-gray-600 inline-flex items-center gap-1.5 hover:text-red-600 hover:border-red-200" data-testid="support-delete">
                  <Trash2 size={14} /> Delete
                </button>
                <div className="flex items-center gap-2">
                  {active.status === "open" && (
                    <button onClick={() => quickUpdate(active.id, "pending")} className="h-9 px-3 border border-gray-200 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50" data-testid="support-mark-pending">
                      <Clock size={14} /> Mark Pending
                    </button>
                  )}
                  {active.status !== "closed" && (
                    <>
                      <button onClick={() => quickUpdate(active.id, "closed")} className="h-9 px-3 border border-gray-200 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50" data-testid="support-close">
                        <Check size={14} /> Close
                      </button>
                      <button onClick={submitReply} disabled={!reply.trim()} className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="support-send-reply">
                        <Send size={14} /> Send Reply & Close
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Transcript modal */}
      {transcript && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setTranscript(null)}>
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="support-transcript-modal">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5">
                  <Sparkles size={12} className="text-violet-600" /> AI chat transcript
                </div>
                <div className="font-display text-base font-bold text-gray-900 mt-0.5">
                  {transcript.ticket.ticket_no} · {transcript.ticket.user_name || transcript.ticket.username}
                </div>
              </div>
              <button onClick={() => setTranscript(null)} className="h-8 w-8 rounded-sm hover:bg-gray-100 flex items-center justify-center text-gray-500">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-gray-50">
              {transcript.items.length === 0 ? (
                <div className="text-center text-sm text-gray-500 py-8">No messages in this session.</div>
              ) : (
                transcript.items.map((m) => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[80%]">
                      <div
                        className={`rounded-sm px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                          m.role === "user" ? "bg-emerald-500 text-white" : "bg-white border border-gray-200 text-gray-800"
                        }`}
                      >
                        {m.text}
                      </div>
                      <div className={`text-[10px] text-gray-400 mt-1 flex items-center gap-1.5 ${m.role === "user" ? "justify-end" : ""}`}>
                        <span>{m.role === "user" ? "Player" : "Maxi (AI)"}</span>
                        <span>·</span>
                        <span>{fmtDateTime(m.created_at)}</span>
                        {m.handoff && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 h-4 rounded-sm bg-amber-100 text-amber-700 text-[9px] font-bold uppercase tracking-wider">
                            Flagged
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
